import { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useDaily, useDailyEvent, useLocalSessionId, useParticipantIds } from "./callTransport"
import { Check, DoorOpen, Megaphone, Minus, Plus, Trash2 } from "lucide-react"
import { Button } from "components/ui/button"
import { CallSelect } from "./CallSelect"
import type { BreakoutMember, BreakoutRoomSpec } from "services/api/meetingsClient"
import { BREAKOUT_END_DELAY_SECONDS, type BreakoutRoomsState } from "./useBreakoutRooms"
import { useBreakoutCountdown } from "./useBreakoutCountdown"
import { MentorAssignmentPicker } from "./MentorAssignmentPicker"

export const MAX_BREAKOUT_ROOMS = 20

/** Daily's `user_id` on a participant: WiseFriend sets `<user id>` for members and `g<hex>` for guests. */
export const identityKeyFromDailyUserId = (dailyUserId: unknown): string | null => {
  const value = typeof dailyUserId === "string" ? dailyUserId.trim() : ""
  if (!value) return null
  if (/^g[0-9a-f]{32}$/i.test(value)) {
    const hex = value.slice(1).toLowerCase()
    return `guest:${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  if (/^\d+$/.test(value)) return `user:${value}`
  return null
}

export type BreakoutParticipant = { sessionId: string; name: string; identityKey: string | null; isLocal: boolean; isOwner: boolean }

/** Live participants with the identity keys the API understands. */
export function useBreakoutParticipants(): BreakoutParticipant[] {
  const daily = useDaily()
  const localSessionId = useLocalSessionId()
  const participantIds = useParticipantIds({ sort: "joined_at" })
  const [participantUpdate, setParticipantUpdate] = useState<{ session_id: string; user_name?: string; user_id?: string; owner?: boolean } | null>(null)
  useDailyEvent("participant-updated", useCallback((event: any) => setParticipantUpdate(event?.participant || null), []))
  return useMemo(() => {
    let map: Record<string, any> = {}
    try {
      map = (daily?.participants() as Record<string, any>) || {}
    } catch {
      map = {}
    }
    const byId = new Map<string, any>()
    Object.values(map).forEach((participant) => {
      if (participant?.session_id) byId.set(participant.session_id, participant)
    })
    return participantIds.map((sessionId) => {
      const participant = participantUpdate?.session_id === sessionId ? participantUpdate : byId.get(sessionId) || {}
      const isLocal = sessionId === localSessionId
      const name = (typeof participant.user_name === "string" && participant.user_name.trim()) || (isLocal ? "You" : "Guest")
      return { sessionId, name, identityKey: identityKeyFromDailyUserId(participant.user_id), isLocal, isOwner: Boolean(participant.owner) }
    })
  }, [daily, localSessionId, participantIds, participantUpdate])
}

/** Round-robin distribution of everyone except the host, like Prebuilt's "Assign automatically". */
export const assignAutomatically = (participants: BreakoutParticipant[], roomCount: number): string[][] => {
  const rooms: string[][] = Array.from({ length: Math.max(1, roomCount) }, () => [])
  participants.filter((participant) => !participant.isOwner && participant.identityKey).forEach((participant, index) => {
    rooms[index % rooms.length].push(participant.sessionId)
  })
  return rooms
}

type SetupState = { roomCount: number; autoJoin: boolean; rooms: string[][]; names?: string[]; mentors?: string[]; mentorshipVersion?: string }

function BreakoutSetup({ breakout, participants, meetingId }: { breakout: BreakoutRoomsState; participants: BreakoutParticipant[]; meetingId?: string }) {
  // Prebuilt starts with manual joining (attendees get a "Please join Room 1" prompt).
  const [state, setState] = useState<SetupState>({ roomCount: 2, autoJoin: false, rooms: [[], []] })
  const assignedFocus = useRef<string | null>(null)
  useLayoutEffect(() => {
    if (!assignedFocus.current) return
    document.getElementById(`call-breakout-assign-${assignedFocus.current}`)?.focus()
    assignedFocus.current = null
  }, [state.rooms])
  const assigned = new Set(state.rooms.flat())
  const mainRoom = participants.filter((participant) => !assigned.has(participant.sessionId))
  const assignable = participants.filter((participant) => participant.identityKey && !participant.isOwner)
  const missingMentor = Boolean(state.mentorshipVersion && state.rooms.some((room, index) => !room.includes(state.mentors?.[index] || "")))

  // Drop participants who left the call from the plan.
  useEffect(() => {
    const live = new Set(participants.map((participant) => participant.sessionId))
    setState((current) => ({ ...current, rooms: current.rooms.map((room) => room.filter((id) => live.has(id))) }))
  }, [participants])

  const setRoomCount = (count: number) => {
    const next = Math.min(MAX_BREAKOUT_ROOMS, Math.max(1, Math.trunc(count)))
    setState((current) => {
      const rooms = current.rooms.slice(0, next)
      while (rooms.length < next) rooms.push([])
      return { ...current, roomCount: next, rooms, names: undefined, mentors: undefined, mentorshipVersion: undefined }
    })
  }
  const assignParticipant = (sessionId: string, destination: string) => {
    assignedFocus.current = sessionId
    setState((current) => {
      const index = destination === "main" ? null : Number(destination)
      if (index !== null && (!Number.isInteger(index) || !current.rooms[index])) return current
      const rooms = current.rooms.map((room) => room.filter((id) => id !== sessionId))
      if (index !== null) rooms[index].push(sessionId)
      return { ...current, rooms }
    })
  }
  const deleteRoom = (roomIndex: number) => {
    setState((current) => {
      if (current.rooms.length <= 1) return current
      const rooms = current.rooms.filter((_, index) => index !== roomIndex)
      return { ...current, roomCount: rooms.length, rooms, names: current.names?.filter((_, i) => i !== roomIndex), mentors: current.mentors?.filter((_, i) => i !== roomIndex) }
    })
  }
  const autoAssign = () => {
    setState((current) => ({ ...current, rooms: assignAutomatically(participants, Math.max(1, current.roomCount)), roomCount: Math.max(1, current.roomCount), names: undefined, mentors: undefined, mentorshipVersion: undefined }))
  }
  const byId = new Map(participants.map((participant) => [participant.sessionId, participant]))
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (breakout.busy || missingMentor || state.rooms.length === 0) return
    const rooms: BreakoutRoomSpec[] = state.rooms.map((ids, index) => ({
      name: state.names?.[index] || `Room ${index + 1}`,
      members: ids
        .map((id) => byId.get(id))
        .filter((participant): participant is BreakoutParticipant => Boolean(participant?.identityKey))
        .map((participant): BreakoutMember => ({ identity_key: participant.identityKey as string, display_name: participant.name, daily_participant_id: participant.sessionId })),
    }))
    if (state.mentorshipVersion) await breakout.startSession(rooms, state.autoJoin, state.mentorshipVersion)
    else await breakout.startSession(rooms, state.autoJoin)
  }

  const participantRow = (participant: BreakoutParticipant, roomIndex: number | null) => (
    <li key={participant.sessionId} className="flex items-center justify-between gap-2 text-xs">
      <span className="min-w-0 break-words">{participant.name}{participant.isLocal ? " (you)" : ""}{participant.isOwner && <span className="ml-1 text-white/50">Host</span>}</span>
      <CallSelect
        id={`call-breakout-assign-${participant.sessionId}`}
        label={`Assign ${participant.name} to`}
        hideLabel
        size="sm"
        value={roomIndex === null ? "main" : String(roomIndex)}
        options={[{ value: "main", label: "Main room" }, ...state.rooms.map((_, index) => ({ value: String(index), label: state.names?.[index] || `Room ${index + 1}` }))]}
        disabled={breakout.busy || !participant.identityKey}
        onChange={(value) => assignParticipant(participant.sessionId, value)}
        className="w-36 shrink-0"
      />
    </li>
  )

  return (
    <form data-testid="call-breakout-setup" onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col text-sm text-white">
      <fieldset disabled={breakout.busy} className="min-h-0 min-w-0 flex-1 space-y-3 overflow-y-auto p-3">
        <div>
          <h3 className="text-base font-semibold">Set up rooms</h3>
          <p className="mt-0.5 text-xs text-white/60">Choose a room beside each name, or assign everyone automatically.</p>
        </div>
        {meetingId && <MentorAssignmentPicker meetingId={meetingId} participants={participants} applied={Boolean(state.mentorshipVersion)} onApply={(plan) => setState((current) => ({ ...current, rooms: plan.rooms, roomCount: plan.rooms.length, names: plan.names, mentors: plan.mentors, mentorshipVersion: plan.version }))} />}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label htmlFor="call-breakout-room-count" className="text-xs font-medium">Number of rooms</label>
          <span className="flex items-center gap-1">
            <button type="button" aria-label="Decrease number of rooms" disabled={state.roomCount <= 1} onClick={() => setRoomCount(state.roomCount - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40"><Minus className="h-3.5 w-3.5" /></button>
            <input id="call-breakout-room-count" type="number" min={1} max={MAX_BREAKOUT_ROOMS} value={state.roomCount} onChange={(event) => setRoomCount(Number(event.target.value) || 1)} className="h-8 w-12 rounded-lg border border-white/15 bg-[#1c2530] px-1 text-center text-sm" />
            <button type="button" aria-label="Increase number of rooms" disabled={state.roomCount >= MAX_BREAKOUT_ROOMS} onClick={() => setRoomCount(state.roomCount + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Button type="button" size="auto" variant="secondary" disabled={!assignable.length} onClick={autoAssign} className="h-8 px-3 text-xs">{assigned.size ? "Reassign automatically" : "Assign automatically"}</Button>
          <span className="text-xs text-white/50">Auto-assign keeps hosts in main.</span>
        </div>
        <label className="flex items-start gap-2 rounded-lg bg-white/5 p-2.5 text-xs">
          <input type="checkbox" checked={state.autoJoin} onChange={(event) => setState((current) => ({ ...current, autoJoin: event.target.checked }))} className="mt-0.5 h-4 w-4 shrink-0 accent-[#f6a35c]" />
          <span><span className="block font-medium">Browser participants join automatically</span><span className="mt-0.5 block text-white/60">{state.autoJoin ? "Assigned browser participants move when you start. Native app participants accept invitations." : "Off: each person accepts an invitation."}</span></span>
        </label>
        <section>
          <h4 className="mb-1 text-xs font-semibold text-white/70">Main room · {mainRoom.length}</h4>
          {mainRoom.length ? <ul data-testid="call-breakout-main-room" className="space-y-1">{mainRoom.map((participant) => participantRow(participant, null))}</ul> : <p className="text-xs text-white/50">Everyone has a room.</p>}
          {mainRoom.some((person) => !person.identityKey) && <p className="mt-1 text-xs text-white/60">Some people are still connecting. Assign them when their room menu is available.</p>}
        </section>
        {state.rooms.map((ids, index) => (
          <section key={index} data-testid={`call-breakout-plan-room-${index + 1}`} className="rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="min-w-0 break-words text-sm font-semibold">{state.names?.[index] || `Room ${index + 1}`} <span className="font-normal text-white/50">· {ids.length} assigned</span></h4>
              <button type="button" aria-label={`Delete room ${index + 1}`} title="Remove room; people stay in main" disabled={state.rooms.length <= 1} onClick={() => deleteRoom(index)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/50 hover:bg-white/10 hover:text-white disabled:opacity-40"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            {ids.length === 0 ? <p className="pb-1 text-xs text-white/40">No one assigned</p> : <ul className="mt-1 space-y-1">{ids.map((id) => byId.get(id)).filter(Boolean).map((participant) => participantRow(participant as BreakoutParticipant, index))}</ul>}
          </section>
        ))}
      </fieldset>
      <div className="max-h-[55%] shrink-0 space-y-2 overflow-y-auto border-t border-white/10 bg-[#18212b] p-3">
        <p data-testid="call-breakout-assignment-summary" className="text-xs text-white/65">{assigned.size} assigned · {mainRoom.length} staying in main</p>
        {breakout.error && <p role="alert" className="text-xs text-red-300">{breakout.error}</p>}
        {state.mentorshipVersion && state.rooms.length === 0 && <p className="text-xs text-[#f6cf93]">No mentor group is ready. Refresh assignments or choose ordinary rooms above.</p>}
        {missingMentor && <p role="alert" className="text-xs text-[#f6cf93]">A mentor is missing from the plan. Refresh mentor assignments before starting.</p>}
        <Button type="submit" disabled={breakout.busy || state.rooms.length === 0 || missingMentor} className="w-full">{breakout.busy ? "Starting rooms…" : "Start breakout session"}</Button>
      </div>
    </form>
  )
}

function BreakoutActive({ breakout, participants }: { breakout: BreakoutRoomsState; participants: BreakoutParticipant[] }) {
  const session = breakout.session
  const [message, setMessage] = useState("")
  const [endChoiceOpen, setEndChoiceOpen] = useState(false)
  const endChoiceWasOpen = useRef(false)
  useLayoutEffect(() => {
    if (endChoiceOpen) document.getElementById("call-breakout-end-delayed")?.focus()
    else if (endChoiceWasOpen.current) document.getElementById("call-breakout-end-trigger")?.focus()
    endChoiceWasOpen.current = endChoiceOpen
  }, [endChoiceOpen])
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [messageSent, setMessageSent] = useState(false)
  const countdown = useBreakoutCountdown(breakout.endsAt)
  if (!session) return null
  const byIdentity = new Map(participants.filter((participant) => participant.identityKey).map((participant) => [participant.identityKey as string, participant]))
  const move = (member: BreakoutMember, roomId: string | null) => void breakout.moveMembers([{ ...member, room_id: roomId }])
  const handleBroadcast = async (event: FormEvent) => {
    event.preventDefault()
    if (breakout.busy || !message.trim()) return
    setMessageSent(false)
    if (await breakout.broadcast(message)) { setMessage(""); setMessageSent(true) }
  }
  const memberRow = (member: BreakoutMember, currentRoomId: string | null) => (
    <li key={member.identity_key || member.daily_participant_id} className="flex items-center justify-between gap-2 text-xs">
      <span className="min-w-0 break-words">{member.display_name || byIdentity.get(member.identity_key)?.name || "Participant"}</span>
      {member.identity_key ? <CallSelect
        id={`call-breakout-move-${currentRoomId || "main"}-${member.identity_key.replace(/[^a-z0-9]+/gi, "-")}`}
        label={`Move ${member.display_name || "participant"} to`}
        hideLabel
        size="sm"
        value=""
        placeholder="Move to…"
        disabled={breakout.busy}
        options={[...(currentRoomId ? [{ value: "main", label: "Main room" }] : []), ...session.rooms.filter((room) => room.id !== currentRoomId).map((room) => ({ value: room.id, label: room.name }))]}
        onChange={(value) => move(member, value === "main" ? null : value)}
        className="w-32 shrink-0"
      /> : <span className="text-white/50">Joining…</span>}
    </li>
  )
  const mainRoomMembers = new Map<string, BreakoutMember>()
  const addMainMember = (member: BreakoutMember) => {
    const key = member.identity_key || member.daily_participant_id
    if (!key) return
    if (member.daily_participant_id) {
      mainRoomMembers.forEach((existing, existingKey) => {
        if (existingKey !== key && existing.daily_participant_id === member.daily_participant_id) mainRoomMembers.delete(existingKey)
      })
    }
    mainRoomMembers.set(key, member)
  }
  const knownMainRoomMembers = breakout.nativePresence ? [] : session.main_room_members ?? session.unassigned
  knownMainRoomMembers.forEach(addMainMember)
  // Daily only lists the room currently joined. Never label breakout participants as main-room
  // attendees when a host visits them; server presence keeps the other rooms visible.
  if (breakout.location.kind === "main" && (!breakout.nativePresence || breakout.presenceVerified)) {
    participants.forEach((participant) => addMainMember({ identity_key: participant.identityKey || "", display_name: participant.name, daily_participant_id: participant.sessionId }))
  }
  return (
    <div data-testid="call-breakout-active" className="h-full min-h-0 overflow-y-auto text-sm text-white">
      <div className="space-y-2 border-b border-white/10 p-3">
        <div>
          <h3 className="text-base font-semibold">Rooms are open</h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-[#f6cf93]"><DoorOpen className="h-3.5 w-3.5 shrink-0" />You&apos;re in {breakout.location.kind === "room" ? breakout.location.roomName : "the main room"}</p>
        </div>
        {breakout.location.kind === "room" && <Button type="button" size="auto" variant="secondary" disabled={breakout.busy} onClick={() => void breakout.returnToMain()} className="h-8 w-full px-3 text-xs">Return to main room</Button>}
        <p className="text-xs text-white/55">{session.auto_join ? "Assigned browser participants join automatically; native app participants accept invitations." : "Assigned people join when they accept."} Use Move to change an assignment.</p>
      </div>
      <div className="space-y-2 p-3">
        {session.rooms.map((room) => {
          const here = breakout.location.kind === "room" && breakout.location.roomId === room.id
          return (
            <section key={room.id} data-testid={`call-breakout-room-${room.position}`} className={`rounded-xl border px-3 py-2 ${here ? "border-[#f6a35c]/60 bg-[#f6a35c]/5" : "border-white/10"}`}>
              <div className="flex items-center justify-between gap-2">
                <h4 className="min-w-0 break-words text-sm font-semibold">{room.name} <span className="font-normal text-white/50">· {room.members.length} assigned</span></h4>
                {here ? <span className="flex shrink-0 items-center gap-1 text-xs text-[#f6cf93]"><Check className="h-3 w-3" />You&apos;re here</span> : <Button type="button" size="auto" variant="secondary" aria-label={`Join ${room.name}`} disabled={breakout.busy} onClick={() => void breakout.joinRoomAsHost(room.id)} className="h-8 shrink-0 gap-1 px-2.5 text-xs"><DoorOpen className="h-3 w-3" />Join</Button>}
              </div>
              {breakout.nativePresence && <p className="mt-1 text-xs text-white/60">{here && breakout.presenceVerified ? `Observed here: ${participants.map(person => person.name).join(', ') || 'No participants reported'}` : 'Actual presence is unknown from this room.'}</p>}
              {room.members.length === 0 ? <p className="mt-1 text-xs text-white/40">No one assigned</p> : <ul className="mt-2 space-y-1">{room.members.map((member) => memberRow(member, room.id))}</ul>}
            </section>
          )
        })}
        <section data-testid="call-breakout-active-main-room" className="rounded-xl border border-white/10 px-3 py-2">
          {breakout.nativePresence && (breakout.location.kind !== "main" || !breakout.presenceVerified) ? <p className="text-xs text-white/60">Main room presence is unknown from this room.</p> : <>
          <h4 className="mb-2 text-sm font-semibold">Main room <span className="font-normal text-white/50">({mainRoomMembers.size})</span></h4>
          {mainRoomMembers.size ? <ul className="space-y-1">{[...mainRoomMembers.values()].map((member) => memberRow(member, null))}</ul> : <p className="text-xs text-white/40">No one here</p>}
          </>}
          {breakout.nativePresence && <div className="mt-2"><p className="text-xs text-white/60">Unassigned intent · {session.unassigned.length} (not presence)</p><ul>{session.unassigned.map(member => memberRow(member, null))}</ul></div>}
        </section>
      </div>
      <div className="space-y-2 border-t border-white/10 bg-[#18212b] p-3">
        <button type="button" aria-expanded={broadcastOpen} aria-controls="call-breakout-message-form" onClick={() => { setBroadcastOpen((open) => !open); setEndChoiceOpen(false) }} className="flex min-h-8 w-full items-center gap-2 rounded-lg text-xs font-medium text-white/80 hover:text-white"><Megaphone className="h-3.5 w-3.5" />Message all rooms<span className="ml-auto">{broadcastOpen ? "−" : "+"}</span></button>
        {broadcastOpen && <form id="call-breakout-message-form" onSubmit={handleBroadcast} className="space-y-2">
          <label htmlFor="call-breakout-broadcast" className="sr-only">Message to all rooms</label>
          <textarea id="call-breakout-broadcast" value={message} onChange={(event) => { setMessage(event.target.value.slice(0, 500)); setMessageSent(false) }} disabled={breakout.busy} rows={2} placeholder="Two minutes left…" className="w-full resize-none rounded-lg border border-white/15 bg-[#1c2530] px-3 py-2 text-sm placeholder:text-white/40" />
          <div className="flex items-center justify-between gap-2"><span className="text-xs text-white/50">{message.length}/500</span><Button type="submit" size="auto" variant="secondary" disabled={breakout.busy || message.trim().length === 0} className="h-8 px-3 text-xs">Send message</Button></div>
        </form>}
        {messageSent && <p role="status" className="flex items-center gap-1 text-xs text-[#f6cf93]"><Check className="h-3 w-3" />Message sent to all rooms</p>}
        {breakout.error && <p role="alert" className="text-xs text-red-300">{breakout.error}</p>}
        {countdown ? (
          <div data-testid="call-breakout-ending" className="flex items-center justify-between gap-2 rounded-lg bg-[#f6a35c]/10 px-3 py-2">
            <span className="text-xs text-[#f6cf93]">Everyone returns in {countdown}</span>
            <Button type="button" size="auto" disabled={breakout.busy} onClick={() => void breakout.endSession(0)} className="h-8 bg-[#d64545] px-3 text-xs text-white hover:bg-[#c03a3a]">End now</Button>
          </div>
        ) : endChoiceOpen ? (
          <section aria-label="End breakout session" data-testid="call-breakout-end-choice" className="space-y-2 rounded-lg bg-white/5 p-3">
            <p className="text-xs text-white/80">Bring everyone back to the main room?</p>
            <div className="flex flex-wrap gap-2">
              <Button id="call-breakout-end-delayed" type="button" size="auto" disabled={breakout.busy} onClick={() => { setEndChoiceOpen(false); void breakout.endSession(BREAKOUT_END_DELAY_SECONDS) }} className="h-8 px-3 text-xs">End in 60 seconds</Button>
              <Button type="button" size="auto" disabled={breakout.busy} onClick={() => { setEndChoiceOpen(false); void breakout.endSession(0) }} className="h-8 bg-[#d64545] px-3 text-xs text-white hover:bg-[#c03a3a]">End immediately</Button>
              <Button type="button" size="auto" variant="secondary" onClick={() => setEndChoiceOpen(false)} className="h-8 px-3 text-xs">Cancel</Button>
            </div>
          </section>
        ) : <Button id="call-breakout-end-trigger" type="button" disabled={breakout.busy} onClick={() => { setEndChoiceOpen(true); setBroadcastOpen(false) }} className="w-full border border-red-400/30 bg-red-400/10 text-red-200 hover:bg-red-400/20">End breakout session</Button>}
      </div>
    </div>
  )
}

function BreakoutAttendeeView({ breakout }: { breakout: BreakoutRoomsState }) {
  const session = breakout.session
  const countdown = useBreakoutCountdown(breakout.endsAt)
  const roomName = breakout.location.kind === "room" ? breakout.location.roomName : "Main room"
  return (
    <div data-testid="call-breakout-attendee" className="h-full space-y-3 overflow-y-auto p-4 text-sm text-white">
      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
        <p className="flex items-center gap-2 font-semibold"><DoorOpen className="h-4 w-4 shrink-0 text-[#f6a35c]" />{roomName}</p>
        {!session ? <p className="mt-2 text-xs text-white/60">The host has not started breakout rooms. You&apos;ll see your invitation here.</p> : breakout.location.kind === "room" ? <>
          <p className="mt-2 text-xs text-white/60">The host will bring everyone back when rooms close.</p>
          <p className="mt-2 text-xs text-white/50">This room is transcribed. Breakout conversations stay separate from the main meeting summary.</p>
        </> : breakout.pendingInvite ? <>
          <p className="my-2 text-xs text-white/70">You&apos;ve been assigned to {breakout.pendingInvite.room_name}. Join when you&apos;re ready.</p>
          <Button type="button" disabled={breakout.busy} onClick={() => void breakout.joinPendingInvite()} className="w-full">{breakout.busy ? "Joining room…" : `Join ${breakout.pendingInvite.room_name}`}</Button>
        </> : <p className="mt-2 text-xs text-white/60">Rooms are open. You&apos;re staying in the main room unless the host assigns you.</p>}
      </div>
      {countdown && <p data-testid="call-breakout-ending" className="text-xs text-[#f6cf93]">Everyone returns to the main room in {countdown}.</p>}
      {breakout.error && <p role="alert" className="text-xs text-red-300">{breakout.error}</p>}
    </div>
  )
}

/** Prebuilt's Breakout tab: session setup and live management for hosts, status for attendees. */
export function BreakoutPanel({ breakout, meetingId }: { breakout: BreakoutRoomsState; meetingId?: string }) {
  const participants = useBreakoutParticipants()
  if (!breakout.isHost) return <BreakoutAttendeeView breakout={breakout} />
  if (breakout.session && breakout.session.status === "active") return <BreakoutActive breakout={breakout} participants={participants} />
  return <BreakoutSetup breakout={breakout} participants={participants} meetingId={meetingId} />
}
